import { useEffect, useState } from "react";
import axios from "axios";
import { getCookies } from "cookies-next";
import { config } from "../../data/config";
import { useTranslation } from 'react-i18next';
import BreadCrump from "../tools/breadcrump";
import HeaderOfPage from "../tools/headerOfPage";
import Swal from 'sweetalert2';

interface CampaignRequest {
    _id: string;
    fullName: string;
    email: string;
    whatsappNumber: string;
    footSide: string;
    appointmentDate: string;
    appointmentTime: string;
    status: string;
    notes: string;
    adminNotes: string;
    rejectionReason?: string;
    createdAt: string;
    user?: {
        name: string;
        email: string;
        phone: string;
    };
}

export default function CampaignRequests() {
    const { t } = useTranslation();
    const [requests, setRequests] = useState<CampaignRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [statusFilter, setStatusFilter] = useState('all');
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        fetchRequests();
    }, [page, statusFilter]);

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const cookies = getCookies();
            const response = await axios.get(`${config.url}/v1/dashboard/campaign/requests`, {
                headers: { Authorization: `Bearer ${cookies.token}` },
                params: {
                    page,
                    limit: 15,
                    status: statusFilter !== 'all' ? statusFilter : undefined
                }
            });

            if (response.data.success) {
                setRequests(response.data.message.docs);
                setTotalPages(response.data.message.totalPages);
            }
        } catch (error) {
            console.error('Error fetching requests:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (requestId: string, newStatus: string) => {
        try {
            // For rejection, ask for a reason
            let rejectionReason = '';
            if (newStatus === 'rejected') {
                const { value: reason, isConfirmed } = await Swal.fire({
                    title: t('Reject Request'),
                    input: 'textarea',
                    inputLabel: t('Reason for rejection'),
                    inputPlaceholder: t('Enter reason for rejection...'),
                    showCancelButton: true,
                    confirmButtonColor: '#e74c3c',
                    cancelButtonColor: '#6c757d',
                    confirmButtonText: t('Reject'),
                    cancelButtonText: t('Cancel'),
                    inputValidator: (value) => {
                        if (!value) {
                            return t('Please enter a reason');
                        }
                        return null;
                    }
                });

                if (!isConfirmed) return;
                rejectionReason = reason;
            } else {
                const result = await Swal.fire({
                    title: t('Confirm Status Change'),
                    text: t('Are you sure you want to change the status?'),
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonColor: '#9B59B6',
                    cancelButtonColor: '#6c757d',
                    confirmButtonText: t('Yes'),
                    cancelButtonText: t('Cancel')
                });

                if (!result.isConfirmed) return;
            }

            const cookies = getCookies();
            await axios.put(`${config.url}/v1/dashboard/campaign/requests/${requestId}/status`,
                { status: newStatus, rejectionReason },
                { headers: { Authorization: `Bearer ${cookies.token}` } }
            );

            const successMessage = newStatus === 'approved'
                ? t('Request approved and email sent')
                : newStatus === 'rejected'
                    ? t('Request rejected and email sent')
                    : t('Status updated successfully');

            Swal.fire({
                icon: 'success',
                title: t('Success'),
                text: successMessage,
                timer: 2000
            });

            fetchRequests();
        } catch (error) {
            console.error('Error updating status:', error);
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: t('Failed to update status')
            });
        }
    };

    const exportPDF = async () => {
        try {
            setExporting(true);
            const cookies = getCookies();
            const response = await axios.get(`${config.url}/v1/dashboard/campaign/export-pdf`, {
                headers: { Authorization: `Bearer ${cookies.token}` },
                params: { status: statusFilter !== 'all' ? statusFilter : 'all' },
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'campaign-requests.pdf');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error exporting PDF:', error);
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: t('Failed to export PDF')
            });
        } finally {
            setExporting(false);
        }
    };

    const getStatusBadge = (status: string) => {
        const styles: { [key: string]: string } = {
            pending: 'bg-yellow-100 text-yellow-800',
            approved: 'bg-green-100 text-green-800',
            rejected: 'bg-red-100 text-red-800',
            scheduled: 'bg-blue-100 text-blue-800',
            completed: 'bg-emerald-100 text-emerald-800',
            canceled: 'bg-gray-100 text-gray-800'
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
                {t(status.charAt(0).toUpperCase() + status.slice(1))}
            </span>
        );
    };

    const getFootSideLabel = (side: string) => {
        const labels: { [key: string]: string } = {
            left: t('Left Foot'),
            right: t('Right Foot'),
            both: t('Both Feet')
        };
        return labels[side] || side;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('fa-IR');
    };

    if (loading && requests.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <BreadCrump data={[
                { name: t('Dashboard'), url: '/dashboard' },
                { name: t('Campaign'), url: '/dashboard/Campaign/Requests' },
                { name: t('Requests'), url: '/dashboard/Campaign/Requests' }
            ]} />

            <HeaderOfPage name={t('Ham-Qadam Campaign Requests')} />

            {/* Filters */}
            <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <label className="font-medium text-gray-700">{t('Status')}:</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500"
                        >
                            <option value="all">{t('All')}</option>
                            <option value="pending">{t('Pending')}</option>
                            <option value="approved">{t('Approved')}</option>
                            <option value="rejected">{t('Rejected')}</option>
                            <option value="scheduled">{t('Scheduled')}</option>
                            <option value="completed">{t('Completed')}</option>
                            <option value="canceled">{t('Canceled')}</option>
                        </select>
                    </div>

                    <button
                        onClick={exportPDF}
                        disabled={exporting}
                        className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                    >
                        {exporting ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                {t('Exporting...')}
                            </>
                        ) : (
                            <>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                {t('Export PDF')}
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Requests Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gradient-to-r from-purple-600 to-purple-800 text-white">
                            <tr>
                                <th className="px-4 py-3 text-right font-medium">{t('Name')}</th>
                                <th className="px-4 py-3 text-right font-medium">{t('WhatsApp')}</th>
                                <th className="px-4 py-3 text-right font-medium">{t('Foot Side')}</th>
                                <th className="px-4 py-3 text-right font-medium">{t('Appointment')}</th>
                                <th className="px-4 py-3 text-right font-medium">{t('Status')}</th>
                                <th className="px-4 py-3 text-right font-medium">{t('Actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {requests.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                                        {t('No requests found')}
                                    </td>
                                </tr>
                            ) : (
                                requests.map((request) => (
                                    <tr key={request._id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <div>
                                                <div className="font-medium text-gray-900">{request.fullName}</div>
                                                <div className="text-sm text-gray-500">{request.user?.email}</div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-gray-700" dir="ltr">
                                            <a href={`https://wa.me/${request.whatsappNumber.replace(/[^0-9]/g, '')}`}
                                                target="_blank"
                                                className="text-green-600 hover:underline">
                                                {request.whatsappNumber}
                                            </a>
                                        </td>
                                        <td className="px-4 py-3 text-gray-700">
                                            {getFootSideLabel(request.footSide)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="text-gray-900">{formatDate(request.appointmentDate)}</div>
                                            <div className="text-sm text-gray-500">{request.appointmentTime}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            {getStatusBadge(request.status)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <select
                                                value={request.status}
                                                onChange={(e) => updateStatus(request._id, e.target.value)}
                                                className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-purple-500"
                                            >
                                                <option value="pending">{t('Pending')}</option>
                                                <option value="approved">{t('Approved')}</option>
                                                <option value="rejected">{t('Rejected')}</option>
                                                <option value="scheduled">{t('Scheduled')}</option>
                                                <option value="completed">{t('Completed')}</option>
                                                <option value="canceled">{t('Canceled')}</option>
                                            </select>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 p-4 border-t">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="px-3 py-1 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                            {t('Previous')}
                        </button>
                        <span className="text-gray-600">
                            {t('Page')} {page} {t('of')} {totalPages}
                        </span>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="px-3 py-1 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                            {t('Next')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
